import { useState, useEffect, useCallback, useRef } from 'react';
import { reverseHnsLookup } from '../../../api/mcpApi';
import { shortenAddress } from '../utils/chatHelpers';

interface HnsEntry {
  username: string;
  display_name?: string;
}

// Module-level cache shared across all hook instances (session-scoped)
const hnsCache = new Map<string, HnsEntry | null>();

// Dedup in-flight requests: track addresses currently being fetched
let pendingFetch: Promise<void> | null = null;
let pendingAddresses: Set<string> = new Set();

async function fetchAndCache(addresses: string[]): Promise<void> {
  const uncached = addresses
    .map((a) => a.toLowerCase())
    .filter((a) => !hnsCache.has(a));

  if (uncached.length === 0) return;

  // Merge into pending set to batch concurrent callers
  uncached.forEach((a) => pendingAddresses.add(a));

  // If a fetch is already in flight, wait for it
  if (pendingFetch) {
    await pendingFetch;
    // After the in-flight fetch completes, check if there are still uncached addresses
    const stillUncached = addresses
      .map((a) => a.toLowerCase())
      .filter((a) => !hnsCache.has(a));
    if (stillUncached.length === 0) return;
  }

  const toFetch = Array.from(pendingAddresses);
  pendingAddresses = new Set();

  pendingFetch = (async () => {
    try {
      const data = await reverseHnsLookup(toFetch);
      const names: Record<string, HnsEntry> = data?.names || {};

      for (const addr of toFetch) {
        const entry = names[addr] || names[addr.toLowerCase()] || null;
        hnsCache.set(addr.toLowerCase(), entry);
      }
    } catch {
      // On failure, mark all as null so we don't re-fetch endlessly
      for (const addr of toFetch) {
        if (!hnsCache.has(addr.toLowerCase())) {
          hnsCache.set(addr.toLowerCase(), null);
        }
      }
    } finally {
      pendingFetch = null;
    }
  })();

  await pendingFetch;
}

/**
 * Hook that resolves Ethereum addresses to HNS @usernames.
 * Uses a module-level cache to avoid redundant API calls.
 */
export function useHnsNames(addresses: string[]) {
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const prevAddressesRef = useRef<string>('');

  useEffect(() => {
    const filtered = addresses.filter(Boolean);
    if (filtered.length === 0) return;

    // Skip if same addresses as last render
    const key = filtered.map((a) => a.toLowerCase()).sort().join(',');
    if (key === prevAddressesRef.current) return;
    prevAddressesRef.current = key;

    // Build map from cache first
    const cached: Record<string, string> = {};
    const uncached: string[] = [];
    for (const addr of filtered) {
      const lower = addr.toLowerCase();
      if (hnsCache.has(lower)) {
        const entry = hnsCache.get(lower);
        if (entry) cached[lower] = `@${entry.username}`;
      } else {
        uncached.push(lower);
      }
    }

    if (Object.keys(cached).length > 0) {
      setNameMap((prev) => ({ ...prev, ...cached }));
    }

    if (uncached.length > 0) {
      setIsLoading(true);
      fetchAndCache(uncached).then(() => {
        const resolved: Record<string, string> = {};
        for (const addr of uncached) {
          const entry = hnsCache.get(addr);
          if (entry) resolved[addr] = `@${entry.username}`;
        }
        if (Object.keys(resolved).length > 0) {
          setNameMap((prev) => ({ ...prev, ...resolved }));
        }
        setIsLoading(false);
      });
    }
  }, [addresses]);

  const getDisplayName = useCallback(
    (address: string): string => {
      if (!address) return '';
      const lower = address.toLowerCase();
      return nameMap[lower] || shortenAddress(address);
    },
    [nameMap]
  );

  return { nameMap, getDisplayName, isLoading };
}
